import { useEffect, useState } from "react";
import { Captions } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const LABELS = {
  en: { on: "Hide captions", off: "Show captions" },
  es: { on: "Ocultar subtítulos", off: "Mostrar subtítulos" },
  he: { on: "הסתרת כתוביות", off: "הצגת כתוביות" },
};

function useCaptionData(src) {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(src)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData(null));
  }, [src]);
  return data;
}

function useCurrentTime(videoRef) {
  const [time, setTime] = useState(0);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onTime = () => setTime(v.currentTime);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("seeked", onTime);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("seeked", onTime);
    };
  }, [videoRef]);
  return time;
}

export const useCaptionsToggle = () => {
  const { lang } = useLanguage();
  const [on, setOn] = useState(lang !== "en");
  useEffect(() => setOn(lang !== "en"), [lang]);
  return [on, setOn];
};

export const CaptionsButton = ({ on, onToggle }) => {
  const { lang } = useLanguage();
  const label = LABELS[lang][on ? "on" : "off"];
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      aria-pressed={on}
      title={label}
      data-no-translate
      className={`inline-flex items-center justify-center w-10 h-10 rounded-full backdrop-blur-md border text-white shadow-lg transition-colors ${
        on ? "bg-teal border-teal" : "bg-navy/70 border-white/20 hover:bg-navy/90"
      }`}
      data-testid="home-video-captions-toggle"
    >
      <Captions className="w-5 h-5" />
    </button>
  );
};

export const VideoCaptions = ({ videoRef, src, on }) => {
  const { lang } = useLanguage();
  const data = useCaptionData(src);
  const time = useCurrentTime(videoRef);
  if (!on || !data) return null;
  const cues = data[lang] || data.en;
  const active = cues.filter((c) => time >= c.start && time < c.end);
  const screen = active.filter((c) => c.kind === "screen").pop();
  const speech = active.filter((c) => c.kind === "speech").pop();
  const dir = lang === "he" ? "rtl" : "ltr";

  return (
    <div className="pointer-events-none absolute inset-0" dir={dir} lang={lang} data-no-translate data-testid="home-video-captions">
      {screen && (
        <div className="absolute top-3 sm:top-6 inset-x-0 flex justify-center px-4">
          <p
            className="max-w-3xl whitespace-pre-line text-center rounded-xl bg-navy/80 backdrop-blur-sm px-3 py-1.5 sm:px-5 sm:py-2.5 text-[11px] sm:text-base lg:text-lg font-semibold text-white border border-teal/40 shadow-lg"
            data-testid="home-video-caption-screen"
          >
            {screen.text}
          </p>
        </div>
      )}
      {speech && (
        <div className="absolute bottom-16 sm:bottom-20 inset-x-0 flex justify-center px-4">
          <p
            className="max-w-4xl text-center rounded-lg bg-black/75 px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-lg lg:text-2xl font-medium leading-snug text-white"
            data-testid="home-video-caption-speech"
          >
            {speech.text}
          </p>
        </div>
      )}
    </div>
  );
};
